const HOVER_COLOR_20 = "rgba(255, 255, 0, 0.2)"

export abstract class PropageWindow<W extends Window = Window> {
  name: string;
  window: W;
  isIframe: boolean;
  selector: string;
  are_all_events_added : boolean;

  constructor(name: string, window: W, selector: string, isIframe: boolean) {
    this.name = name;
    this.window = window;
    this.isIframe = isIframe;
    this.selector = selector;
    this.are_all_events_added = false;
  }

  abstract init(): Promise<void>;
  abstract jump(id: string): void;
  abstract add_events(): void;
  abstract find_id(id: string): HTMLElement[];
  abstract get_id_from_event_target(event_target: HTMLElement): string;
  abstract highlight(id: string, state: boolean): void;

  set_hover_style(elem: HTMLElement, low_opacity: boolean): void {
    if(low_opacity) elem.style.setProperty("opacity", "1"); // This is because PDFJs has a low opacity by default...
    elem.style.setProperty("background-color", HOVER_COLOR_20, "important");
    elem.style.setProperty("box-shadow", "0 2px 20px " + HOVER_COLOR_20 , "important");
  }

  get_event_elements(element: HTMLElement): HTMLElement[] {
    return [...element.querySelectorAll(this.selector)] as HTMLElement[];
  }

  unset_hover_style(elem: HTMLElement): void {
    elem.style.backgroundColor = "";
    elem.style.boxShadow = "";
  }

  /**
  * Gets the closest element that is the closest from the top of the viewport.
  * @param {Array} elements An array of elements to check.
  * @returns The closest element, if element is not empty.
  */
  get_closest_element(elements: HTMLElement[]): HTMLElement {
    let closest_element;
    let closest_distance = Number.MAX_VALUE;

    // We get the the closest element from the top of the viewport
    elements.forEach(el => {
      const rect = el.getBoundingClientRect();
      const distance = Math.abs(rect.top);

      if (distance < closest_distance) {
        closest_distance = distance;
        closest_element = el;
      }
    });

    if (!closest_element) {
      throw new Error();
    }

    return closest_element;
  }

  add_hover_events(elems: HTMLElement[]): void {
    if (elems) { // Condition necessary when everything has not loaded yet
      elems.forEach(elem => {
        elem.addEventListener("mouseover", event => {
          this.highlight(this.get_id_from_event_target(event.target as HTMLElement), true);
          this.post_message("highlight", event.target as HTMLElement, ["true"]);
        });
        elem.addEventListener("mouseout", event => {
          this.highlight(this.get_id_from_event_target(event.target as HTMLElement), false);
          this.post_message("highlight", event.target as HTMLElement, ["false"]);
        });
      });
    }
  }

  add_scroll_events(elements: HTMLElement[], scrolled_element: HTMLElement): void {
    scrolled_element.addEventListener('wheel', () => {
      try {
        // And send the info to the other iframes
        let closest_element = this.get_closest_element(elements);
        if (closest_element) this.post_message("jump", closest_element);
      } catch(e) {
        console.error("No events added on " + this.name + ".")
        console.error(e) ;
      }
    });
  }

  post_message(type : string, element : HTMLElement, opts : string[] = []): void {
    let id_event_target : string ;
    id_event_target = this.get_id_from_event_target(element) ;
    if(id_event_target) {
      let message : string[] = [type, id_event_target, this.name].concat(opts) ;
      this.window.parent.postMessage(message.join(" "));
      // console.debug("Message posted:", message.join(" "));
    }
  }

  add_hover_and_add_scroll_events(elements: HTMLElement[], window_element: HTMLElement) {
   this.add_scroll_events(elements, window_element);
   this.add_hover_events(elements);
  }

  all_events_added() {
    console.debug("All events added on " + this.name + ".") ;
    this.are_all_events_added = true ;
  }
}
